import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Loader2, UserPlus, CheckCircle2, RefreshCcw, Clock, TrendingUp, Mail, Phone, Bell, ChevronDown } from "lucide-react";

const STATUS_STYLES = {
  pending:   "bg-amber-50 text-amber-800 border-amber-200",
  started:   "bg-amber-50 text-amber-800 border-amber-200",
  completed: "bg-sky-50 text-sky-800 border-sky-200",
  recovered: "bg-emerald-50 text-emerald-800 border-emerald-200",
  expired:   "bg-gray-100 text-gray-600 border-gray-200",
};

export default function AbandonedSignups() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/abandoned-signups/stats"),
        api.get("/admin/abandoned-signups", { params: filter ? { status: filter } : {} }),
      ]);
      setStats(s.data);
      setRows(r.data.signups || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="space-y-5" data-testid="abandoned-signups-panel">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Lifecycle</div>
          <h2 className="text-2xl font-extrabold tracking-tighter font-[Outfit] text-gray-900">Abandoned signups</h2>
          <p className="text-sm text-gray-600">Visitors who started signing up but didn&apos;t finish — with auto SMS + email at 1h / 24h / 72h.</p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-extrabold px-3 h-9 rounded-full border border-gray-200 bg-white hover:bg-gray-50"
          data-testid="abandoned-signups-refresh"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="abandoned-signups-stats">
          <Stat icon={UserPlus}    label="Started"    value={stats.started}     tint="bg-gray-50 text-gray-800" />
          <Stat icon={CheckCircle2} label="Completed" value={stats.completed}   tint="bg-emerald-50 text-emerald-800" />
          <Stat icon={Bell}        label="Recovered"  value={stats.recovered}   tint="bg-amber-50 text-amber-800" />
          <Stat icon={TrendingUp}  label="Conversion" value={`${stats.conversion_rate}%`} tint="bg-sky-50 text-sky-800" />
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {["", "pending", "recovered", "completed", "expired"].map((s) => (
          <button
            key={s || "all"}
            type="button"
            onClick={() => setFilter(s)}
            data-testid={`signup-filter-${s || "all"}`}
            className={`px-3 h-7 rounded-full text-[11px] font-extrabold uppercase tracking-[0.14em] border ${filter === s ? "bg-[#047857] text-white border-[#047857]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
          >
            {s || "all"}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading && (
          <div className="p-10 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        )}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-gray-500" data-testid="signup-empty">No signup attempts yet.</div>
        )}
        {!loading && rows.length > 0 && (
          <div className="divide-y divide-gray-100" data-testid="signup-list">
            {rows.map((r) => {
              const isOpen = expanded === r.attempt_id;
              return (
                <div key={r.attempt_id} data-testid={`signup-row-${r.attempt_id}`}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : r.attempt_id)}
                    className="w-full text-left px-4 sm:px-5 py-3 hover:bg-gray-50 flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-extrabold text-gray-900 truncate">{r.name || "—"}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.14em] rounded-full border ${STATUS_STYLES[r.recovery_status] || "bg-gray-100"}`}>
                          {r.recovery_status}
                        </span>
                        {r.discount_sent_at && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-emerald-800">
                            ✓ {r.discount_code || "WELCOME10"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 truncate flex items-center gap-3 mt-0.5">
                        {r.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {r.email}</span>}
                        {r.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[11px] text-gray-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</div>
                      <div className="mt-0.5 text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500">{(r.reminders_sent || []).length} reminders</div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-4 pt-1 bg-gray-50/60 border-t border-gray-100 text-[12px] text-gray-700 grid sm:grid-cols-2 gap-3" data-testid={`signup-detail-${r.attempt_id}`}>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">Source</div>
                        <div className="font-bold">{r.source || "—"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">Discount</div>
                        <div className="font-bold">{r.discount_code || "WELCOME10"}{r.discount_sent_at ? " · sent" : ""}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">Completed</div>
                        <div className="font-bold">{r.completed_at ? new Date(r.completed_at).toLocaleString() : "—"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">Reminder timeline</div>
                        <ul className="mt-1 space-y-1">
                          {(r.reminders_sent || []).map((rem, i) => (
                            <li key={i} className="text-[12px]"><span className="font-mono text-emerald-700">{rem.milestone}</span> · {rem.channel} · <span className="text-gray-500">{rem.at ? new Date(rem.at).toLocaleString() : ""}</span></li>
                          ))}
                          {(!r.reminders_sent || r.reminders_sent.length === 0) && <li className="text-gray-500">No reminders fired yet</li>}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className={`rounded-2xl border border-gray-200 px-4 py-3 ${tint}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold opacity-70 flex items-center gap-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-0.5 text-2xl font-extrabold tabular-nums tracking-tight font-[Outfit]">{value}</div>
    </div>
  );
}
